import {OnDestroy, AfterViewInit, ElementRef, EventEmitter, Output, Directive} from '@angular/core';

@Directive()
export abstract class AbstractPrintComponent implements AfterViewInit, OnDestroy {
  @Output() init = new EventEmitter<void>();
  
  private activeTimeouts: Set<any> = new Set();
  private readonly MAX_RETRY_ATTEMPTS = 50;
  private readonly RETRY_INTERVAL = 1000;

  constructor(private elRef: ElementRef) {}

  ngAfterViewInit(): void {
    const images: NodeListOf<HTMLImageElement> = this.elRef.nativeElement.querySelectorAll('img');
    const svgs: NodeListOf<SVGElement> = this.elRef.nativeElement.querySelectorAll('svg');
    const qrCodes: NodeListOf<HTMLElement> = this.elRef.nativeElement.querySelectorAll('qrcode');

    const loadingImages = Array.from(images).filter(img => !img.complete);
    const loadingSVGs = Array.from(svgs).filter(svg => {
      const {width, height} = svg.getBoundingClientRect();
      return width === 0 || height === 0;
    });

    // Verification of qrcode content (wait for images in each qrcode tag)
    const qrcodePromises = Array.from(qrCodes).map(qr => new Promise<void>((resolve) => {
      let attemptCount = 0;
      
      const checkQRCodeContent = () => {
        if(attemptCount >= this.MAX_RETRY_ATTEMPTS) {
          console.warn('QR Code loading timeout after', this.MAX_RETRY_ATTEMPTS, 'attempts');
          resolve(); // Resolve anyway to avoid blocking the entire process
          return;
        }
        
        attemptCount++;
        
        const innerImg = qr.querySelector('img');
        const innerCanvas = qr.querySelector('canvas');
        const innerSvg = qr.querySelector('svg');
        let ctx: CanvasRenderingContext2D;
        let isLoaded = false;

        // Checks whether the SVG inside is loaded
        if(innerSvg) {
          const {width, height} = innerSvg.getBoundingClientRect();
          if(width !== 0 && height !== 0) {
            isLoaded = true;
          }
        }
        
        // Checks whether the image or canvas inside is loaded
        if((innerImg && innerImg.complete) || 
           (innerCanvas && (ctx = innerCanvas.getContext('2d'), ctx.getImageData(0, 0, 1, 1).data.length > 0))) {
          isLoaded = true;
        }
        
        if(isLoaded) {
          resolve();
        } else {
          // Re-check after a short delay
          const timeoutId = setTimeout(checkQRCodeContent, this.RETRY_INTERVAL);
          this.activeTimeouts.add(timeoutId);
        }
      };
      
      checkQRCodeContent();
    }));

    const allLoadingPromises = [
      ...loadingImages.map(img => new Promise(resolve => {
        img.addEventListener('load', resolve, {once: true});
        img.addEventListener('error', resolve, {once: true}); // Handle errors too
      })),
      ...loadingSVGs.map(svg => new Promise(resolve => {
        svg.addEventListener('load', resolve, {once: true});
        svg.addEventListener('error', resolve, {once: true}); // Handle errors too
      })),
      ...qrcodePromises
    ];

    Promise.all(allLoadingPromises).then(() => {
      this.init.emit();
    });
  }

  ngOnDestroy(): void {
    // Clear all active timeouts to prevent memory leaks
    this.activeTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.activeTimeouts.clear();
    
    //In case the component is destroyed before the event is emitted
    this.init.complete();
  }
}

